import { api } from "@/redux/api";

import {
	ForgotPasswordRequestSchema,
	ForgotPasswordType,
} from "../pages/ForgotPassword/schema/forgot-password";
import {
	FetchMeResponseSchema,
	LoginRequestSchema,
	LoginResponse,
	LoginResponseSchema,
	LoginType,
	User,
} from "../pages/Login/schema/login";
import {
	AcceptInvitationBody,
	AcceptInvitationRequestSchema,
	SetPasswordBody,
	SetPasswordRequestSchema,
} from "../pages/AcceptInvitation/schema/set-password";
import { ConfirmEmailType } from "../pages/ConfirmEmail/schema/confirm-email";
import {
	ChangePasswordBody,
	ChangePasswordRequestSchema,
} from "../pages/ChangePassword/schema/set-password";

export const authApi = api.injectEndpoints({
	endpoints: (builder) => ({
		fetchMe: builder.query<User, string>({
			query: (token) => ({
				url: "users/me",
				headers: {
					Authorization: `Bearer ${token}`,
				},
				responseSchema: FetchMeResponseSchema,
			}),
			providesTags: ["Me"],
		}),
		fetchMeByInvitationToken: builder.query<User, string>({
			query: (token) => ({
				url: `users/me-by-invitation-token/${token}`,
				responseSchema: FetchMeResponseSchema,
			}),
		}),
		login: builder.mutation<LoginResponse, LoginType>({
			query: (body) => ({
				url: "users/login",
				method: "POST",
				body,
				requestSchema: LoginRequestSchema,
				responseSchema: LoginResponseSchema,
			}),
			invalidatesTags: ["Roles"],
		}),
		downloadResults: builder.mutation<Blob, { token: string }>({
			query: (body) => ({
				url: "users/download-results",
				method: "POST",
				body,
				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		downloadResultsPdf: builder.mutation<Blob, void>({
			query: () => ({
				url: "users/download-results-pdf",
				method: "POST",
				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		resetPassword: builder.mutation<void, ForgotPasswordType>({
			query: (body) => ({
				url: "users/reset-password",
				method: "POST",
				body,
				requestSchema: ForgotPasswordRequestSchema,
			}),
		}),
		setPassword: builder.mutation<LoginResponse, SetPasswordBody>({
			query: (body) => ({
				url: "users/set-password",
				method: "POST",
				body,
				requestSchema: SetPasswordRequestSchema,
			}),
		}),
		changePasswordWithToken: builder.mutation<void, ChangePasswordBody>({
			query: (body) => ({
				url: "users/set-password",
				method: "POST",
				body,
				requestSchema: ChangePasswordRequestSchema,
			}),
		}),
		acceptInvitation: builder.mutation<
			{
				status?: string;
				accessToken?: string;
				refreshToken?: string;
				otpToken?: string;
				isRememberMe?: boolean;
				user?: User;
				email?: string;
			},
			AcceptInvitationBody
		>({
			query: (body) => ({
				url: "users/accept-invitation",
				method: "POST",
				body,
				requestSchema: AcceptInvitationRequestSchema,
			}),
		}),
		authTwoFactorCode: builder.mutation<LoginResponse, { code: string; token: string }>({
			query: ({ code, token }) => ({
				url: "2fa/authenticate",
				method: "POST",
				body: { code },
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}),
		}),
		confirmEmail: builder.mutation<void, ConfirmEmailType>({
			query: (body) => ({
				url: "users/confirm-email",
				method: "POST",
				body,
			}),
		}),
	}),
});

export const {
	useLoginMutation,
	useResetPasswordMutation,
	useSetPasswordMutation,
	useLazyFetchMeQuery,
	useAuthTwoFactorCodeMutation,
	useAcceptInvitationMutation,
	useConfirmEmailMutation,
	useChangePasswordWithTokenMutation,
	useFetchMeByInvitationTokenQuery,
	useDownloadResultsMutation,
	useDownloadResultsPdfMutation,
} = authApi;
export const authApiReducer = authApi.reducer;
export const authApiMiddleware = authApi.middleware;
